import { Formik, Form } from "formik";
import { useState } from "react";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import LogoIcon from "@/core/components/logo-icon";
import { Button } from "@/core/components/ui/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/core/components/ui/input-otp";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { useBreakpoints } from "@/core/component-hooks/use-breakpoints";
import { cn } from "@/core/utils/utils";
import { InitiateVerificationResponse, IPayout } from "@/payouts/payouts";
import { cleanupPayoutVerification, useVerifyCode } from "@/payouts/query";

const verifyCodeSchema = Yup.object().shape({
  verification_code: Yup.string().required("Code is required"),
});

interface VerifyCodeProps {
  payout: IPayout;
  verificationData: InitiateVerificationResponse;
  onVerified: (redirectUrl: string, creditEnabled: boolean) => void;
  onResendCode: () => void;
}

export const VerifyCode = ({
  payout,
  verificationData,
  onVerified,
  onResendCode,
}: VerifyCodeProps) => {
  const { payoutEndpoint } = useParams();
  const { isSmallMobile } = useBreakpoints();

  const [autoSubmitEnabled, setAutoSubmitEnabled] = useState(true);
  const { mutate } = useVerifyCode(payoutEndpoint);

  const handleVerify = async (values: { verification_code: string }) => {
    if (values.verification_code.length !== 6) return;

    setAutoSubmitEnabled(false);

    return mutate(values.verification_code, {
      onSuccess: (response) => {
        const { redirect_url } = response.data;
        onVerified(
          redirect_url,
          verificationData?.credit_payment_enabled ?? false,
        );
      },
      onError: (error) => {
        toast.error("Failed to verify code", {
          description:
            error instanceof Error ? error.message : "Please try again",
        });
        setAutoSubmitEnabled(true);
      },
    });
  };

  const handleResendCode = () => {
    cleanupPayoutVerification(payout.endpoint);
    onResendCode();
    toast.success("Code resent");
  };

  return (
    <Stack
      align="center"
      fullWidth
      className="m-auto min-h-screen max-w-md bg-card p-8"
      gap="xl"
    >
      <LogoIcon />
      <Typography variant="subtitleW1" className="text-center">
        You received a referral payment from {verificationData?.business_name}
      </Typography>
      <Formik
        initialValues={{ verification_code: "" }}
        validationSchema={verifyCodeSchema}
        validateOnMount
        onSubmit={handleVerify}
      >
        {({ values, isSubmitting, isValid, setFieldValue }) => (
          <Form className="w-full">
            <div className="my-4">
              <InputOTP
                maxLength={6}
                value={values.verification_code}
                onChange={(value) => {
                  setFieldValue("verification_code", value);
                  if (autoSubmitEnabled && value.length === 6) {
                    handleVerify({ verification_code: value });
                  }
                }}
                containerClassName={cn("justify-center", {
                  "gap-1": isSmallMobile,
                  "gap-3": !isSmallMobile,
                })}
              >
                {Array.from({ length: 6 }).map((_, index) => (
                  <InputOTPGroup key={index}>
                    <InputOTPSlot
                      index={index}
                      className={cn(
                        "border-input bg-card text-center text-lg shadow-sm transition-colors focus:border-primary focus-visible:ring-1 focus-visible:ring-ring",
                        {
                          "h-10 w-10": isSmallMobile,
                          "h-12 w-12": !isSmallMobile,
                        },
                      )}
                    />
                  </InputOTPGroup>
                ))}
              </InputOTP>
            </div>
            <Stack gap="md" justify="center" fullWidth className="px-12">
              <Button
                type="submit"
                disabled={!isValid}
                fullWidth
                loading={isSubmitting}
              >
                Verify
              </Button>
              <Button variant="text" onClick={handleResendCode} fullWidth>
                Resend Code
              </Button>
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};
